// playbookRunSelectionUtils.js

import { getMissionTaskPersonKey } from 'missions/taskmodal/missionTaskModalUtils';
import {
  NODE_MISSION_STATUS,
  NODE_MISSION_STATUS_LABELS,
  isMissionRunComplete,
  isMissionTaskAssignee,
} from 'node/missions/mission/utils/missionRunUtils';
import {
  getPlaybookCurrentStepIndex,
  getPlaybookFocusSteps,
  isPlaybookFocusStepUnfinished,
} from 'node/missions/mission/focus/playbookFocusUtils';

/**
 * getPlaybookCurrentIncompleteTask
 *
 * Returns the process-current unfinished active-path task for a run,
 * or null when the run has no unfinished work.
 *
 * @param {object|null} missionRun - Normalized playbook run.
 * @returns {object|null} Current unfinished task.
 */
export function getPlaybookCurrentIncompleteTask(missionRun) {
  if (!missionRun || isMissionRunComplete(missionRun)) return null;

  const steps = getPlaybookFocusSteps(missionRun);
  const currentIndex = getPlaybookCurrentStepIndex(steps);
  if (currentIndex < 0) return null;

  const currentTask = steps[currentIndex]?.task || null;
  if (!currentTask || !isPlaybookFocusStepUnfinished(currentTask)) return null;
  return currentTask;
}

/**
 * isPlaybookRunLive
 *
 * @param {object|null} entry - Mission entry with missionRun / missionStatus.
 * @returns {boolean} Whether the entry has a launched, incomplete run.
 */
export function isPlaybookRunLive(entry) {
  return Boolean(entry?.missionRun) && !isMissionRunComplete(entry.missionRun);
}

/**
 * getPlaybookSwitcherMeta
 *
 * Builds the quiet status line for a title-switcher picker row.
 *
 * @param {object|null} entry - Mission entry.
 * @returns {string} Meta label.
 */
export function getPlaybookSwitcherMeta(entry) {
  if (!entry?.missionRun) return 'Not started';

  const status = String(entry?.missionStatus || '').trim();
  if (status === NODE_MISSION_STATUS.COMPLETE || isMissionRunComplete(entry.missionRun)) {
    return 'Complete';
  }

  const complete = Number(entry?.missionProgress?.complete || 0);
  const total = Number(entry?.missionProgress?.total || 0);
  const statusLabel = NODE_MISSION_STATUS_LABELS[status] || 'In Progress';

  if (total > 0) {
    return `${statusLabel} · ${complete}/${total}`;
  }

  return statusLabel;
}

/**
 * getPlaybookRunLaunchMs
 *
 * @param {object|null} missionRun - Normalized playbook run.
 * @returns {number} Launch timestamp ms.
 */
function getPlaybookRunLaunchMs(missionRun) {
  return Number(missionRun?.launchedAtMs || missionRun?.updatedAtMs || 0) || 0;
}

/**
 * getPlaybookCurrentTaskReadyMs
 *
 * @param {object|null} task - Current unfinished task.
 * @param {object|null} missionRun - Parent run for launch fallback.
 * @returns {number} Ready/launch timestamp used for "earliest current" ordering.
 */
function getPlaybookCurrentTaskReadyMs(task, missionRun) {
  const readyAtMs = Number(task?.readyAtMs || 0) || 0;
  if (readyAtMs > 0) return readyAtMs;
  return getPlaybookRunLaunchMs(missionRun);
}

/**
 * resolveDefaultPlaybookTemplateKey
 *
 * Picks which launched playbook to show when the user has not chosen one
 * for this node session.
 *
 * Order:
 * 1. Live run where this user is assigned on the current incomplete step
 * 2. Live run with the earliest current incomplete step
 * 3. Most recently launched live run
 * 4. Most recently completed run when nothing is live
 *
 * @param {object} options
 * @param {object[]} options.missionEntries - Template entries with optional runs.
 * @param {object|null} [options.user] - Current signed-in user.
 * @returns {string} Template key, or empty string when no runs exist.
 */
export function resolveDefaultPlaybookTemplateKey({
  missionEntries = [],
  user = null,
} = {}) {
  const entriesWithRuns = (Array.isArray(missionEntries) ? missionEntries : [])
    .filter((entry) => Boolean(entry?.missionRun) && String(entry?.template?.key || '').trim());

  if (!entriesWithRuns.length) return '';

  const liveEntries = entriesWithRuns.filter((entry) => isPlaybookRunLive(entry));

  if (liveEntries.length > 0) {
    const userKey = getMissionTaskPersonKey(user);
    if (userKey) {
      const assignedLive = liveEntries.find((entry) => {
        const currentTask = getPlaybookCurrentIncompleteTask(entry.missionRun);
        return currentTask && isMissionTaskAssignee(currentTask, user);
      });
      if (assignedLive) {
        return String(assignedLive.template.key).trim();
      }
    }

    const withCurrent = liveEntries
      .map((entry) => {
        const currentTask = getPlaybookCurrentIncompleteTask(entry.missionRun);
        return {
          entry,
          readyMs: currentTask
            ? getPlaybookCurrentTaskReadyMs(currentTask, entry.missionRun)
            : Number.POSITIVE_INFINITY,
        };
      })
      .sort((left, right) => {
        if (left.readyMs !== right.readyMs) return left.readyMs - right.readyMs;
        return (
          getPlaybookRunLaunchMs(right.entry.missionRun)
          - getPlaybookRunLaunchMs(left.entry.missionRun)
        );
      });

    const earliestCurrent = withCurrent.find((item) => Number.isFinite(item.readyMs));
    if (earliestCurrent) {
      return String(earliestCurrent.entry.template.key).trim();
    }

    const newestLive = [...liveEntries].sort((left, right) => (
      getPlaybookRunLaunchMs(right.missionRun) - getPlaybookRunLaunchMs(left.missionRun)
    ))[0];
    return String(newestLive?.template?.key || '').trim();
  }

  const newestComplete = [...entriesWithRuns].sort((left, right) => (
    getPlaybookRunLaunchMs(right.missionRun) - getPlaybookRunLaunchMs(left.missionRun)
  ))[0];

  return String(newestComplete?.template?.key || '').trim();
}

/**
 * resolveSelectedPlaybookEntry
 *
 * Resolves the playbook entry to render: session pick when still valid,
 * otherwise the default algorithm.
 *
 * @param {object} options
 * @param {object[]} options.missionEntries - Template entries with optional runs.
 * @param {string} [options.sessionSelectedTemplateKey] - Per-node session selection.
 * @param {object|null} [options.user] - Current signed-in user.
 * @returns {object|null} Selected mission entry, or null when no runs exist.
 */
export function resolveSelectedPlaybookEntry({
  missionEntries = [],
  sessionSelectedTemplateKey = '',
  user = null,
} = {}) {
  const entriesWithRuns = (Array.isArray(missionEntries) ? missionEntries : [])
    .filter((entry) => Boolean(entry?.missionRun) && String(entry?.template?.key || '').trim());

  if (!entriesWithRuns.length) return null;

  const sessionKey = String(sessionSelectedTemplateKey || '').trim();
  if (sessionKey) {
    const sessionEntry = entriesWithRuns.find((entry) => (
      String(entry?.template?.key || '').trim() === sessionKey
    ));
    if (sessionEntry) return sessionEntry;
  }

  const defaultKey = resolveDefaultPlaybookTemplateKey({
    missionEntries: entriesWithRuns,
    user,
  });
  if (!defaultKey) return entriesWithRuns[0] || null;

  return entriesWithRuns.find((entry) => (
    String(entry?.template?.key || '').trim() === defaultKey
  )) || entriesWithRuns[0] || null;
}
